import {
  coachDisplayName,
  formatCoachingTime,
  scheduledDurationMinutes,
} from '@efdigital/ll-addon-coaching';
import CoachCardAvatar from '@efdigital/ll-addon-coaching/components/coach-card/avatar';
import {
  formatDate,
  formatTimeRange,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import type { CoachingSessionStatus } from '@efdigital/ll-addon-data';
import { Heading } from '@efdigital/ll-addon-ui';
import type { QuotaPoolSummary } from '@efdigital/ll-cdk-coaching-sessions-api-types';
import { EmptyState } from '@efdigital/ll-xceed-v2/components/application/empty-state';
import AnnouncementBanner from '@efdigital/ll-xceed-v2/components/coaching/coachee/announcement-banner.gts';
import DashboardSessionRow from '@efdigital/ll-xceed-v2/components/coaching/coachee/dashboard-session-row.gts';
import GiveFeedbackBanner from '@efdigital/ll-xceed-v2/components/coaching/coachee/give-feedback-banner.gts';
import { GiveFeedbackModal } from '@efdigital/ll-xceed-v2/components/coaching/coachee/give-feedback-modal.gts';
import { OnboardingChecklist } from '@efdigital/ll-xceed-v2/components/coaching/coachee/onboarding-checklist';
import GoalTracker from '@efdigital/ll-xceed-v2/components/coaching/goal-tracker.gts';
import { QuickStartLink } from '@efdigital/ll-xceed-v2/components/coaching/quick-start-link.gts';
import { queryCoachProfileByUserId } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import { queryCoachingSessionsByCoachingPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import type { CoachingCoacheeCohortIndexModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/index';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import type CohortAnnouncementsService from '@efdigital/ll-xceed-v2/services/cohort-announcements';
import type SessionService from '@efdigital/ll-xceed-v2/services/session';
import { cohortShowsAnnouncements } from '@efdigital/ll-xceed-v2/utils/coaching/announcements';
import { cohortScopeKey } from '@efdigital/ll-xceed-v2/utils/coaching/scope-key';
import {
  COACHEE_PENDING_CONFIRMATION_KEYS,
  COACHEE_UPCOMING_KEYS,
  filterCoacheeActionRequired,
} from '@efdigital/ll-xceed-v2/utils/coaching/session-current-categories';
import { PAGE_WRAPPER } from '@efdigital/ll-xceed-v2/utils/layout';
import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

function toDate(date: string): Date {
  return new Date(date);
}

interface IndexSignature {
  Args: {
    model: CoachingCoacheeCohortIndexModel;
    controller: unknown;
  };
}

const MAX = 20;

/**
 * The pool's total: the config allocation plus any admin grants (`granted`,
 * already counted in `remaining`). Using `allocated` alone let a grant push
 * `remaining` past the total ("10h 30min / 10h") and the bar past 100%.
 *
 * `?? 0` stays despite the type: a staging/prod sessions API older than
 * ll-cdk-coaching#65 omits `granted`, and this app can deploy ahead of it.
 */
function poolTotal(quota: QuotaPoolSummary): number {
  return quota.allocated + (quota.granted ?? 0);
}

/**
 * Total scheduled minutes across sessions awaiting coach confirmation — the
 * amount held back from "remaining" but not yet booked. Skips any session
 * whose start/end can't be parsed into a valid duration rather than letting
 * one bad record take out the whole balance display.
 */
function sumPendingMinutes(
  sessions: {
    startTime: string;
    endTime: string;
    status: CoachingSessionStatus;
  }[]
): number {
  return sessions
    .filter((session) =>
      COACHEE_PENDING_CONFIRMATION_KEYS.includes(session.status)
    )
    .reduce((total, session) => {
      try {
        return total + scheduledDurationMinutes(session);
      } catch {
        return total;
      }
    }, 0);
}

function creditPercentage(remaining: number, total: number): number {
  return total ? Math.round((remaining / total) * 100) : 0;
}

function creditPercentageStyle(remaining: number, total: number): string {
  return `width: ${creditPercentage(remaining, total)}%`;
}

// The pending segment sits after the remaining one on the same bar, so its
// width is capped to whatever room is left rather than trusting
// `remaining + pending <= allocated` to hold exactly — a session added or
// cancelled between the two reads it's built from should never overflow the
// bar.
function pendingPercentageStyle(
  remaining: number,
  pending: number,
  total: number
): string {
  if (!total) {
    return 'width: 0%';
  }
  const remainingPercentage = creditPercentage(remaining, total);
  const pendingPercentage = Math.min(
    Math.round((pending / total) * 100),
    100 - remainingPercentage
  );
  return `width: ${Math.max(pendingPercentage, 0)}%`;
}

function formatCount(value: number): string {
  return `${value}`;
}

function progressValueText(
  quota: QuotaPoolSummary,
  pending: number | undefined,
  formatValue: (value: number) => string
): string {
  const pendingText = pending
    ? `, ${formatValue(pending)} pending confirmation`
    : '';

  return `${formatValue(quota.remaining)} remaining${pendingText}, of ${formatValue(
    poolTotal(quota)
  )} allocated`;
}

const PercentageBar = <template>
  <div class="card shadow-sm mb-4 border rounded-4 overflow-hidden">
    <div
      class="card-body d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3 gap-lg-4 p-3 p-md-4 min-w-0"
    >
      <div class="flex-shrink-0 min-w-0">
        <p
          class="small text-body-secondary fw-semibold mb-1"
        >{{@balanceLabel}}</p>
        <p class="mb-1 text-break">
          <span class="display-4 fw-bold lh-1 text-body-emphasis">{{@formatValue
              @quota.remaining
            }}</span>
          <span class="fs-5 text-body-secondary">/
            {{@formatValue (poolTotal @quota)}}</span>
        </p>
        <p class="small text-body-secondary mb-0">{{@remainingLabel}}</p>
      </div>
      <div class="flex-grow-1 min-w-0 px-0">
        {{! template-lint-disable no-inline-styles }}
        {{! Track colour: `.ll-chart-track` — light gray in light mode, the
            pie's unused-arc token in dark mode so it still reads on charcoal. }}
        <div
          class="progress mb-3 rounded-pill ll-chart-track"
          role="progressbar"
          aria-valuenow={{@quota.remaining}}
          aria-valuemin="0"
          aria-valuemax={{poolTotal @quota}}
          aria-label={{@remainingLabel}}
          aria-valuetext={{progressValueText @quota @pendingValue @formatValue}}
        >
          {{! No rounding on the segments themselves: `.progress` crops to its
              own `rounded-pill`, so a pill on the first segment only curves
              its inner edge away from the pending one sitting beside it. }}
          <div
            class="progress-bar bg-primary"
            style={{creditPercentageStyle @quota.remaining (poolTotal @quota)}}
          ></div>
          {{#if @pendingValue}}
            <div
              class="progress-bar ll-pattern-pending"
              style={{pendingPercentageStyle
                @quota.remaining
                @pendingValue
                (poolTotal @quota)
              }}
            ></div>
          {{/if}}
          {{! template-lint-enable no-inline-styles }}
        </div>
        <div class="d-flex flex-wrap gap-3 gap-md-4">
          <span
            class="d-inline-flex align-items-center gap-2 fw-bold text-body"
          >
            {{! A filled check, not a plain colour dot: "confirmed" is a
                state, not just a hue, so it survives a colourblind pass. }}
            <iconify-icon
              inline
              icon="ph:check-circle-fill"
              class="text-primary flex-shrink-0"
              aria-hidden="true"
            />
            {{@formatValue @quota.remaining}}
            {{@remainingLabel}}
          </span>
          {{#if @pendingValue}}
            <span
              class="d-inline-flex align-items-center gap-2 text-body-secondary"
            >
              <span
                class="ll-pattern-pending ll-pending-swatch rounded-1 flex-shrink-0"
                aria-hidden="true"
              ></span>
              {{@formatValue @pendingValue}}
              pending — awaiting confirmation
            </span>
          {{else if @upcomingCount}}
            <span
              class="d-inline-flex align-items-center gap-2 text-body-secondary"
            >
              <span
                class="ll-pattern-pending ll-pending-swatch rounded-1 flex-shrink-0"
                aria-hidden="true"
              ></span>
              {{@upcomingCount}}
              pending
            </span>
          {{/if}}
        </div>
      </div>
      {{yield}}
    </div>
  </div>
</template> satisfies TOC<{
  Args: {
    quota: QuotaPoolSummary;
    upcomingCount: number;
    /**
     * Amount held for not-yet-confirmed bookings, in the same unit as
     * `quota`. Renders a second, patterned bar segment and swaps the
     * session-count pending line for a precise one. Omit where a
     * unit-accurate pending amount isn't available (e.g. chemistry calls) —
     * `upcomingCount` still shows as a fallback.
     */
    pendingValue?: number;
    balanceLabel: string;
    remainingLabel: string;
    formatValue: (value: number) => string;
  };
  Blocks: {
    /** A booking call to action, sat at the end of the balance row. */
    default: [];
  };
}>;

export default class CoachingCoacheeCohortIndexComponent extends Component<IndexSignature> {
  @service declare coach: CoachService;
  @service declare cohortAnnouncements: CohortAnnouncementsService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare session: SessionService;

  get canAddGoal(): boolean {
    return Boolean(this.args.model.coachingPlan);
  }

  /**
   * Whether any shortlisted or chosen coach can still have a chemistry call
   * booked, which decides whether the balance bar's button points at the
   * shortlist or at the full list of coaches. Deliberately not which coach:
   * that is the coachee's choice to make on the page.
   */
  get hasBookableChemistryCoach(): boolean {
    const { planner } = this.args.model;

    return planner.chemistryAndChosenPartnerships.some((partnership) =>
      planner.canBookChemistrySession(partnership.coachUserId)
    );
  }

  get announcementPreview() {
    if (!cohortShowsAnnouncements(this.args.model.cohort.css)) {
      return null;
    }

    void this.cohortAnnouncements.version;
    const scopeKey = cohortScopeKey(
      this.args.model.program_id,
      this.args.model.cohort_id
    );
    if (this.cohortAnnouncements.scopeKey !== scopeKey) {
      return null;
    }

    const latestUnread = this.cohortAnnouncements.latestUnread;
    if (!latestUnread) {
      return null;
    }

    return {
      latest: latestUnread,
      unreadCount: this.cohortAnnouncements.unreadCount,
    };
  }

  goToAddGoals = () => {
    void this.router.transitionTo(
      'coaching.coachee.cohort.goals',
      this.args.model.program_id,
      this.args.model.cohort_id,
      { queryParams: { add: 'true' } }
    );
  };

  async getUpcomingSessions() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoachingPlan(this.args.model.coachingPlan!.id!, [
        ...COACHEE_UPCOMING_KEYS,
        ...COACHEE_PENDING_CONFIRMATION_KEYS,
      ])
    );
    // Confirmed (scheduled) sessions take priority for "next session" -- an
    // awaiting-confirmation booking isn't settled yet, so it should only surface
    // here when there is no confirmed session ahead of it.
    const next =
      sessions.content.data.find((session) =>
        COACHEE_UPCOMING_KEYS.includes(session.status)
      ) ?? sessions.content.data[0];
    return {
      count: Math.min(sessions.content.data.length, MAX),
      more: sessions.content.data.length > MAX,
      next,
      nextIsPendingConfirmation: next
        ? COACHEE_PENDING_CONFIRMATION_KEYS.includes(next.status)
        : false,
      pendingMinutes: sumPendingMinutes(sessions.content.data),
    };
  }

  async getActionRequired() {
    // Same source as the Sessions nav badge: the planner already holds every
    // session for the plan, so querying again here would be a second request
    // for data in memory - and two sources could disagree, which is exactly
    // what a badge and a card sitting on one screen must not do.
    const actionRequired = filterCoacheeActionRequired(
      this.args.model.planner.allSessions
    );

    // Only the soonest one is shown. Listing them all made this card taller
    // than the next-session card beside it, and a Bootstrap row stretches both
    // to the taller of the two - so a second row was enough to distort the
    // pair. One row plus a count keeps the height fixed however many are
    // waiting, and the count keeps "View all" meaningful.
    //
    // The planner's query sorts by startTime ascending and the filter
    // preserves that order, so the first is the most urgent rather than the
    // most recent.
    const next = actionRequired[0];
    const coach = next
      ? await this.store
          .request(queryCoachProfileByUserId(next.coachUserId, 'published'))
          .then((result) => result.content.data[0] ?? null)
      : null;

    return {
      count: actionRequired.length,
      next: next ? { session: next, coach: coach ?? undefined } : null,
    };
  }

  async getNextSessionCoach() {
    const coachUserId =
      this.args.model.planner.chosenCoachPartnership()?.coachUserId;
    if (!coachUserId) {
      return null;
    }

    const result = await this.store.request(
      queryCoachProfileByUserId(coachUserId, 'published')
    );
    return result.content.data[0] ?? null;
  }

  // Invoked from the template, so it must be a bound arrow-function field.
  getFeedbackCoach = async () => {
    const coachUserId =
      this.args.model.planner.partnershipAwaitingFeedback?.coachUserId;
    if (!coachUserId) {
      return null;
    }

    const result = await this.store.request(
      queryCoachProfileByUserId(coachUserId, 'published')
    );
    return result.content.data[0] ?? null;
  };

  getStats = async () => {
    const [upcomingSessions, actionRequired, nextSessionCoach] =
      await Promise.all([
        this.getUpcomingSessions(),
        this.getActionRequired(),
        this.getNextSessionCoach(),
      ]);

    return {
      upcomingSessions,
      actionRequired,
      nextSessionCoach,
    };
  };

  <template>
    <Await @promise={{(this.getStats)}}>
      <:success as |stats|>

        <div class={{PAGE_WRAPPER}}>
          <Heading
            @size="lg"
            @text="Welcome, {{this.session.cached.displayName}}"
            @secondary="Here's your coaching journey at a glance."
            @class="mb-4 ll-page-heading"
          />

          <section class="mb-4">
            <QuickStartLink
              @route="coaching.coachee.cohort.help"
              @icon="ph:lightbulb"
              @title="Coaching guide"
              @description="How coaching works, choosing your coach, and how session credits and scheduling work."
              @wide={{true}}
            />
          </section>

          {{#if @model.planner.partnershipAwaitingFeedback}}
            <Await @promise={{(this.getFeedbackCoach)}}>
              <:success as |feedbackCoach|>
                {{#if feedbackCoach}}
                  <GiveFeedbackModal
                    @partnership={{@model.planner.partnershipAwaitingFeedback}}
                    @planner={{@model.planner}}
                    @coachName={{coachDisplayName feedbackCoach}}
                    @autoOpen={{true}}
                    as |showFeedbackModal|
                  >
                    <GiveFeedbackBanner
                      @coachName={{coachDisplayName feedbackCoach}}
                      @onClick={{showFeedbackModal}}
                    />
                  </GiveFeedbackModal>
                {{/if}}
              </:success>
              <:error>
                {{! A failed coach lookup just means no feedback banner. }}
              </:error>
            </Await>
          {{/if}}

          {{#if this.announcementPreview}}
            <AnnouncementBanner
              @announcement={{this.announcementPreview.latest}}
              @unreadCount={{this.announcementPreview.unreadCount}}
            />
          {{/if}}

          <OnboardingChecklist
            @hasPublishedAnswer={{@model.hasPublishedAnswer}}
            @planner={{@model.planner}}
            class="mb-4"
          />

          <div class="row g-3 g-lg-4 mb-4">
            <div class="col-12 col-xl-6">
              <section aria-labelledby="next-session-heading" class="h-100">
                {{#if stats.upcomingSessions.next}}
                  <div
                    class="card border shadow-sm h-100 rounded-4 overflow-hidden"
                  >
                    <div class="card-body d-flex flex-column h-100 p-3 p-md-4">
                      <h2
                        id="next-session-heading"
                        class="gud-eyebrow text-body-secondary mb-3 d-flex flex-wrap align-items-center gap-2"
                      >Next session
                        {{#if stats.upcomingSessions.nextIsPendingConfirmation}}
                          <span
                            class="badge rounded-pill text-bg-warning"
                          >Pending confirmation</span>
                        {{/if}}</h2>
                      <div class="d-flex align-items-center gap-3 mb-3 min-w-0">
                        {{#if stats.nextSessionCoach}}
                          <CoachCardAvatar
                            @coach={{stats.nextSessionCoach}}
                            @size="sm"
                            @avatarUrl={{stats.nextSessionCoach.photo}}
                            class="text-primary"
                          />
                        {{/if}}
                        <div class="min-w-0">
                          <h3
                            class="h5 fw-bold mb-1 text-body-emphasis text-capitalize text-break"
                          >{{stats.upcomingSessions.next.sessionType}}</h3>
                          <p class="small text-body-secondary mb-0 text-break">
                            {{formatDate
                              (toDate stats.upcomingSessions.next.startTime)
                            }}
                            ·
                            {{formatTimeRange
                              (toDate stats.upcomingSessions.next.startTime)
                              (toDate stats.upcomingSessions.next.endTime)
                            }}
                          </p>
                        </div>
                      </div>
                      {{#if stats.nextSessionCoach}}
                        <div class="border-top pt-3 mt-auto">
                          <p class="fw-bold mb-0 text-body-emphasis">
                            {{coachDisplayName stats.nextSessionCoach}}
                          </p>
                        </div>
                      {{/if}}
                    </div>
                  </div>
                {{else}}
                  <div
                    class="card border shadow-sm rounded-4 h-100 overflow-hidden"
                  >
                    <div
                      class="card-body d-flex flex-column justify-content-center p-3 p-md-4"
                    >
                      <h2
                        id="next-session-heading"
                        class="gud-eyebrow mb-2"
                      >Next session</h2>
                      <p class="text-body-secondary mb-0">
                        You have no upcoming sessions booked.
                      </p>
                    </div>
                  </div>
                {{/if}}
              </section>
            </div>

            <div class="col-12 col-xl-6">
              <section aria-labelledby="attention-heading" class="h-100">
                <div
                  class="card border shadow-sm rounded-4 h-100 overflow-hidden"
                >
                  <div class="card-body p-3 p-md-4">
                    <div
                      class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
                    >
                      <h2
                        id="attention-heading"
                        class="h5 fw-bold mb-0 d-inline-flex flex-wrap align-items-center gap-2 min-w-0 text-body-emphasis"
                      >
                        Need your attention
                        {{#if stats.actionRequired.count}}
                          {{! The count sits flush against the tags: on its own
                              line the surrounding whitespace renders, which
                              widens the badge and shifts the digit off centre. }}
                          <span
                            class="badge rounded-pill text-bg-warning d-inline-flex align-items-center justify-content-center"
                            aria-label="{{stats.actionRequired.count}} need attention"
                          >{{stats.actionRequired.count}}</span>
                        {{/if}}
                      </h2>
                      <LinkTo
                        @route="coaching.coachee.cohort.sessions.action-required"
                        class="btn btn-link link-primary text-decoration-none p-0 fw-semibold flex-shrink-0"
                      >View all<span class="visually-hidden">
                          sessions needing attention</span></LinkTo>
                    </div>

                    {{#if stats.actionRequired.next}}
                      <DashboardSessionRow
                        @session={{stats.actionRequired.next.session}}
                        @coach={{stats.actionRequired.next.coach}}
                      />
                    {{else}}
                      <EmptyState @message="Nothing needs your attention." />
                    {{/if}}
                  </div>
                </div>
              </section>
            </div>
          </div>

          {{#if @model.planner.coachHasBeenChosen}}
            <PercentageBar
              @quota={{@model.planner.quota.coaching}}
              @upcomingCount={{stats.upcomingSessions.count}}
              @pendingValue={{stats.upcomingSessions.pendingMinutes}}
              @balanceLabel="Time remaining"
              @remainingLabel="remaining"
              @formatValue={{formatCoachingTime}}
            >
              {{#let
                (@model.planner.chosenCoachPartnership)
                as |chosenCoachPartnership|
              }}
                {{#if chosenCoachPartnership}}
                  {{#if
                    (@model.planner.canBookCoachingSession
                      chosenCoachPartnership.coachUserId
                    )
                  }}
                    <LinkTo
                      @route="coaching.coachee.cohort.sessions.book-session"
                      class="btn btn-primary rounded-pill flex-shrink-0 align-self-stretch align-self-lg-center"
                    >
                      <iconify-icon
                        inline
                        icon="ph:calendar-plus"
                        aria-hidden="true"
                      />
                      Book session
                    </LinkTo>
                  {{/if}}
                {{/if}}
              {{/let}}
            </PercentageBar>
          {{else}}
            <PercentageBar
              @quota={{@model.planner.quota.chemistry}}
              @upcomingCount={{stats.upcomingSessions.count}}
              @balanceLabel="Chemistry calls"
              @remainingLabel="chemistry calls remaining"
              @formatValue={{formatCount}}
            >
              {{#if @model.planner.hasChemistryQuota}}
                {{! Both destinations are lists, so the coachee picks who the
                    chemistry call is with. Linking straight to a booking meant
                    choosing for them - whichever partnership came first, since
                    the shortlist has no primary - while the button only said
                    "Book session". }}
                {{#if this.hasBookableChemistryCoach}}
                  <LinkTo
                    @route="coaching.coachee.cohort.coaches.shortlist"
                    class="btn btn-primary rounded-pill flex-shrink-0 align-self-stretch align-self-lg-center"
                  >
                    <iconify-icon
                      inline
                      icon="ph:calendar-plus"
                      aria-hidden="true"
                    />
                    Book session
                  </LinkTo>
                {{else}}
                  {{! Nobody shortlisted yet, so the shortlist would be empty -
                      browse the full list instead. }}
                  <LinkTo
                    @route="coaching.coachee.cohort.coaches"
                    class="btn btn-primary rounded-pill flex-shrink-0 align-self-stretch align-self-lg-center"
                  >
                    <iconify-icon
                      inline
                      icon="ph:calendar-plus"
                      aria-hidden="true"
                    />
                    Book session
                  </LinkTo>
                {{/if}}
              {{/if}}
            </PercentageBar>
          {{/if}}

          <GoalTracker
            @goals={{@model.goals}}
            @milestones={{@model.milestones}}
            @canAddGoal={{this.canAddGoal}}
            @onAddGoals={{this.goToAddGoals}}
            @programId={{@model.program_id}}
            @cohortId={{@model.cohort_id}}
            @showActivePreview={{true}}
          />
        </div>
      </:success>
    </Await>
  </template>
}
